/**
 * studentActivitiesViewModel — `(reduxState) → props` de l'onglet Activités de l'élève (patron A).
 *
 * Ce que lit la branche élève de `TodoListScreen.js` (pas de prop `student`, l'élève connecté) :
 * - « À faire » = `user.todo_activities`, dans l'ordre servi (`todo_index`) : la liste se range,
 *   elle ne trie pas ;
 * - « Faites » = `getTodaysFinishedActivities(user)` — `recentFinishedActivities`, le port du parent ;
 * - le bloc « Faites » est là dès que l'élève a des activités faites OU de l'XP (`:711`), même vide :
 *   c'est son chemin vers ses réalisations ;
 * - le chargement est `authService.getUser` (`:90-98`, la relecture et le tirer-pour-rafraîchir).
 *
 * L'élève est le compte connecté : `currentUser.object` est toujours là quand l'onglet est monté. Il
 * n'y a donc pas de « premier chargement » à couvrir d'un squelette — une relecture fait tourner le
 * bouton de la barre de titre, comme la flèche du legacy, et une erreur de relecture laisse la liste
 * en place (le legacy n'en disait rien). La page vide et l'erreur ne valent que sans compte.
 */
import { createSelector } from "reselect"

import { toUIActivities, type ApiActivity } from "../../../adapters/parent/Activities/activityAdapter"
import { hasStudentWorked } from "../../../adapters/student/Activities/studentActivitiesAdapter"
import { recentFinishedActivities } from "../../parent/Activities/activitiesViewModel"
import { type UIActivity } from "../../../ui/types"
import { type UIActivityDurationTone } from "../../../ui/types/activity"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type StudentActivitiesViewModel = {
    /** Les activités à faire, dans l'ordre de l'élève. */
    activities: UIActivity[]
    /** Les activités récemment faites — cinq au plus. */
    doneActivities: UIActivity[]
    /** Le bloc « Faites » est affiché (des activités faites, ou de l'XP). */
    showDone: boolean
    /** Rien à montrer encore : le compte n'est pas chargé. */
    loading: boolean
    /** Une relecture du compte est en cours. */
    refreshing: boolean
    /** Erreur bloquante — seulement sans compte. */
    error: unknown | null
}

/** Redux root is still untyped in this app — keep the VM boundary explicit. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

/** Au-delà de cette part du temps prévu, le chrono passe à l'orange (`CHRONO_ORANGE_LIMIT`, `TimerContainer.js`). */
export const CHRONO_OVER_PERCENT = 95

/**
 * Le ton du chrono d'une activité à faire, sur les secondes servies (`duration`) et non sur les
 * minutes affichées : la règle de `calculateTimerColor` appelée par `TodoActivityBadges.js:31-66`.
 * Sans temps passé (absent ou zéro), pas de ton : le legacy laissait le chrono gris.
 */
export function durationToneOf(
    activity: Pick<ApiActivity, "duration" | "estimated_duration"> | null | undefined
): UIActivityDurationTone | undefined {
    const spent = activity?.duration
    if (spent == null || spent === 0) return undefined
    const progress = (spent * 100) / ((activity?.estimated_duration ?? 0) * 60)
    return progress < CHRONO_OVER_PERCENT ? "on_time" : "over"
}

function withDurationTones(activities: UIActivity[], todos: ApiActivity[] | null | undefined): UIActivity[] {
    const byId = new Map((todos ?? []).map((raw) => [String(raw?.id), raw]))
    return activities.map((activity) => {
        const durationTone = durationToneOf(byId.get(String(activity.id)))
        return durationTone != null ? { ...activity, durationTone } : activity
    })
}

/**
 * Les entrées sont les champs lus, pas le compte entier : chaque geste du jeu (pétales, titre…)
 * recopie `currentUser.object` sans toucher à ces listes, et ne doit pas rejouer les adaptateurs.
 * Les listes ont leur propre sélecteur : une relecture du compte (ses transitions de chargement)
 * ne les refait pas.
 */
const selectActivityLists = createSelector(
    [
        (state: ReduxState) => selectDisplayedStudent(state)?.todo_activities,
        (state: ReduxState) => selectDisplayedStudent(state)?.activities,
        (state: ReduxState) => state?.data?.staticData?.school_subjects,
    ],
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    (todos: any, finished: any, subjects: any) => ({
        activities: withDurationTones(toUIActivities(todos, subjects ?? []), todos),
        doneActivities: toUIActivities(recentFinishedActivities(finished), subjects ?? []),
    })
)

export const studentActivitiesViewModel = createSelector(
    [
        (state: ReduxState) => selectDisplayedStudent(state) != null,
        selectActivityLists,
        (state: ReduxState) => selectDisplayedStudent(state)?.xp,
        (state: ReduxState) => state?.api?.getUser,
    ],
    (
        hasAccount: boolean,
        { activities, doneActivities },
        xp: number | null | undefined,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        req: any
    ): StudentActivitiesViewModel => {
        const requesting = !!req?.request

        return {
            activities,
            doneActivities,
            showDone: doneActivities.length > 0 || hasStudentWorked({ xp }),
            loading: !hasAccount && requesting,
            refreshing: hasAccount && requesting,
            error: hasAccount ? null : (req?.error ?? null),
        }
    }
)

/**
 * La phrase d'un rangement que le serveur n'a pas pris — la liste est déjà revenue à sa place. Un trou
 * réseau se dit comme tel (le legacy ouvrait sa boîte « Pas de connexion Internet ») ; un refus,
 * jamais attendu, ne promet rien d'autre que de réessayer.
 */
export function todoReorderNotice(failure: "offline" | "rejected" | null): string | null {
    if (failure === "offline") return "Pas de connexion : ton ordre n'a pas été enregistré."
    if (failure === "rejected") return "Ton ordre n'a pas pu être enregistré. Réessaie."
    return null
}
